import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RESOURCES_TYPES } from 'src/features/resources-assignment/constants';
import { getInheritedContainers } from 'src/features/resources-assignment/utils';

const pageInfoDefaultState = {
    totalRowCount: 0,
    totalRowCountWithFilter: 0,
    nextCursor: undefined,
    hasNextPage: false,
};

export const lastAssignedResourcesDefaultState = {
    assignedResources: {
        assignedCompanies: [],
        assignedCompaniesIds: [],
        assignedSites: [],
        assignedSitesIds: [],
        futureAllCompany: false,
        futureAllSite: false,
        assignedContainersIds: [],
    },
    selectedAllCompany: false,
    selectedAllSite: false,
};

export const initialState = {
    activeConnectedAppId: '',
    activeRoleId: '',
    showResourceAssignmentScreen: false,
    showResourceAssignmentSuccessAlert: false,
    selectedMenu: '',
    level: null,
    container: '',
    selectedRoleInfo: {
        isAdminRole: false,
        roleName: '',
    },
    companies: {
        rows: [],
        inheritedRows: [],
        inheritedRowsCount: 0,
        pageInfo: pageInfoDefaultState,
        isLoading: false,
        errors: '',
    },
    levels: {
        data: [],
        isLoading: false,
        errors: '',
    },
    sites: {
        rows: [],
        inheritedRows: [],
        inheritedRowsCount: 0,
        pageInfo: pageInfoDefaultState,
        isLoading: false,
        errors: '',
    },
    selectedCompanies: [],
    selectedCompaniesIds: [],
    selectedSites: [],
    selectedSitesIds: [],
    grantAccessToAllFutureResources: {
        [RESOURCES_TYPES.CONTAINER]: false,
        [RESOURCES_TYPES.COMPANY]: false,
        [RESOURCES_TYPES.SITE]: false,
    },
    selectAllResources: {
        [RESOURCES_TYPES.CONTAINER]: false,
        [RESOURCES_TYPES.COMPANY]: false,
        [RESOURCES_TYPES.SITE]: false,
    },
    // Last assigned & saved resources in DB
    lastAssignedResources: lastAssignedResourcesDefaultState,
    containers: {
        data: [],
        isLoading: false,
        errors: '',
    },
    inheritedContainers: {
        data: [],
    },
};
// eslint-disable-next-line no-unused-vars
const stateKeysToResetOnRAScreenToggle = [
    'activeConnectedAppId',
    'activeRoleId',
    'selectedMenu',
    'level',
    'container',
    'grantAccessToAllFutureResources',
    'selectAllResources',
    'companies',
    'sites',
    'selectedCompaniesIds',
    'selectedSitesIds',
    'selectedCompanies',
    'selectedSites',
    'inheritedContainers',
    'containers',
];

export const resourcesAssignmentSliceId = 'resources_assignment';

const slice = createSlice({
    name: resourcesAssignmentSliceId,
    initialState,
    reducers: {
        changeSelectedRoleInfo: (state, action) => {
            state.selectedRoleInfo = action.payload;
        },
        changeSelectedMenu: (state, action) => {
            state.selectedMenu = action.payload;
        },
        toggleResourceSelectionScreen: (state, action) => {
            const { showResourceAssignmentScreen } = action.payload;
            state.showResourceAssignmentScreen = showResourceAssignmentScreen;
        },
        changeActiveConnectedAppId: (state, action) => {
            const { activeConnectedAppId } = action.payload;
            state.activeConnectedAppId = activeConnectedAppId;
        },
        changeActiveRoleId: (state, action) => {
            const { activeRoleId } = action.payload;
            state.activeRoleId = activeRoleId;
        },
        changeShowResourceAssignmentSuccessAlert: (state, action) => {
            const { showResourceAssignmentSuccessAlert } = action.payload;
            state.showResourceAssignmentSuccessAlert = showResourceAssignmentSuccessAlert;
        },
        prepopulateAssignedResources: (state, action) => {
            const {
                assignedResources: {
                    assignedCompanies,
                    assignedCompaniesIds,
                    assignedSites,
                    assignedSitesIds,
                    assignedContainersIds,
                    futureAllCompany,
                    futureAllSite,
                },
                selectedAllCompany,
                selectedAllSite,
            } = action.payload;

            state.lastAssignedResources = action.payload;

            state.selectedCompanies = assignedCompanies || [];
            state.selectedCompaniesIds = assignedCompaniesIds || [];

            state.selectedSites = assignedSites || [];
            state.selectedSitesIds = assignedSitesIds || [];
            state.container = assignedContainersIds?.[0] || '';

            state.grantAccessToAllFutureResources[RESOURCES_TYPES.COMPANY] = futureAllCompany || false;
            state.grantAccessToAllFutureResources[RESOURCES_TYPES.SITE] = futureAllCompany || futureAllSite || false;

            state.selectAllResources[RESOURCES_TYPES.COMPANY] = futureAllCompany || selectedAllCompany || false;
            state.selectAllResources[RESOURCES_TYPES.SITE] =
                futureAllCompany || futureAllSite || selectedAllSite || false;
        },
        changeLevel: (state, action) => {
            state.level = action.payload;
        },
        changeContainer: (state, action) => {
            state.container = action.payload;
        },
        changeGrantAccessToAllFutureResources: (state, action) => {
            const { resourceType, value } = action.payload;

            state.grantAccessToAllFutureResources = {
                ...state.grantAccessToAllFutureResources,
                [resourceType]: value,
            };
        },
        changeSelectAllResources: (state, action) => {
            const { resourceType, value } = action.payload;

            state.selectAllResources = {
                ...state.selectAllResources,
                [resourceType]: value,
            };
        },
        resetResourceAssignmentStateOnDrawerClose: () => initialState,
        resetStateOnResourceAssignmentScreenToggle: state => {
            stateKeysToResetOnRAScreenToggle.forEach(key => {
                state[key] = initialState[key];
            });
        },
        // companies saga reducers
        fetchCompanies(state) {
            state.companies = {
                ...state.companies,
                rows: [],
                isLoading: true,
                errors: '',
            };
        },
        fetchCompaniesSuccess(state, action) {
            const { rows, pageInfo } = action.payload;

            state.companies = {
                ...state.companies,
                rows,
                isLoading: false,
                errors: '',
                pageInfo: {
                    ...state.companies.pageInfo,
                    ...pageInfo,
                },
            };
        },
        fetchCompaniesFailure(state, action) {
            const { error } = action.payload;
            state.companies = {
                ...state.companies,
                rows: [],
                isLoading: false,
                errors: error,
            };
        },
        // Inherited companies saga reducers
        fetchInheritedCompanies(state) {
            state.companies = {
                ...state.companies,
                inheritedRows: [],
            };
            state.containers.isLoading = true;
        },
        fetchInheritedCompaniesSuccess(state, action) {
            const { rows } = action.payload;

            state.companies = {
                ...state.companies,
                inheritedRows: rows?.map(company => company.id),
            };
            state.containers.isLoading = false;
        },
        fetchInheritedCompaniesFailure(state, action) {
            const { error } = action.payload;
            state.companies = {
                ...state.companies,
                inheritedRows: [],
                errors: error,
            };
            state.containers.isLoading = false;
        },
        fetchLevels(state) {
            state.levels.isLoading = true;
            state.containers.isLoading = true;
            state.levels.errors = '';
        },
        fetchLevelsSuccess(state, action) {
            state.levels.isLoading = false;
            state.levels.data = action.payload;
            state.levels.errors = '';
        },
        fetchLevelsFailure(state, action) {
            state.levels.isLoading = false;
            state.containers.isLoading = false;
            state.levels.errors = action.payload;
        },
        fetchAllContainers(state) {
            state.containers = {
                data: [],
                isLoading: true,
                errors: '',
            };
        },
        fetchAllContainersSuccess(state, action) {
            state.containers = {
                data: action.payload,
                isLoading: false,
                errors: '',
            };
        },
        fetchAllContainersFailure(state, action) {
            state.containers = {
                data: [],
                isLoading: false,
                errors: action.payload,
            };
        },
        fetchContainersByLevels(state) {
            // it will be changed in future. This is just for reference purpose
            state.containers = {
                data: [],
                isLoading: true,
                errors: '',
            };
        },
        fetchContainersByLevelsSuccess(state, action) {
            // it will be changed in future. This is just for reference purpose
            state.containers = {
                data: action.payload,
                isLoading: false,
                errors: '',
            };
        },
        fetchContainersByLevelsFailure(state, action) {
            // it will be changed in future. This is just for reference purpose
            state.containers = {
                data: [],
                isLoading: false,
                errors: action.payload,
            };
        },
        changeInheritedContainers(state, action) {
            const { container, containerAll } = action.payload;
            const inheritedContainers = getInheritedContainers(containerAll, container);
            state.inheritedContainers.data = inheritedContainers;
        },
        changeSelectedCompaniesIds(state, action) {
            state.selectedCompaniesIds = action.payload;
        },
        changeSelectedCompanies(state, action) {
            state.selectedCompanies = action.payload;
        },
        changeInheritedCompanies(state, action) {
            state.companies.inheritedRows = action.payload;
        },
        changeInheritedCompaniesCount(state, action) {
            state.companies.inheritedRowsCount = action.payload;
        },
        // sites saga reducers
        fetchSites(state) {
            state.sites = {
                ...state.sites,
                isLoading: true,
                errors: '',
                pageInfo: {
                    ...state.sites.pageInfo,
                },
            };
        },
        fetchSitesSuccess(state, action) {
            const { rows, pageInfo } = action.payload;

            state.sites = {
                ...state.sites,
                rows,
                isLoading: false,
                errors: '',
                pageInfo: {
                    ...state.sites.pageInfo,
                    ...pageInfo,
                },
            };
        },
        // Inherited sites saga reducers
        fetchInheritedSites(state) {
            state.sites = {
                ...state.sites,
                inheritedRows: [],
            };
            state.containers.isLoading = true;
        },
        fetchInheritedSitesSuccess(state, action) {
            const { rows } = action.payload;

            state.sites = {
                ...state.sites,
                inheritedRows: rows?.map(site => site.id) || [],
            };
            state.containers.isLoading = false;
        },
        fetchInheritedSitesFailure(state, action) {
            const { error } = action.payload;
            state.sites = {
                ...state.sites,
                inheritedRows: [],
                errors: error,
            };
            state.containers.isLoading = false;
        },
        changeInheritedSites(state, action) {
            state.sites.inheritedRows = action.payload;
        },
        changeInheritedSitesCount(state, action) {
            state.sites.inheritedRowsCount = action.payload;
        },
        fetchSitesFailure(state, action) {
            const { error } = action.payload;

            state.sites = {
                ...state.sites,
                isLoading: false,
                errors: error,
                pageInfo: {
                    ...state.sites.pageInfo,
                },
            };
        },
        changeSelectedSitesIds(state, action) {
            state.selectedSitesIds = action.payload;
        },
        changeSelectedSites(state, action) {
            state.selectedSites = action.payload;
        },
        saveOrUpdateAssignedResource: () => {},
    },
});

const selectResourcesAssignmentSliceState = state => state[resourcesAssignmentSliceId];

// Common Selectors
const selectSelectedMenu = createSelector([selectResourcesAssignmentSliceState], state => state.selectedMenu);
const selectLevel = createSelector([selectResourcesAssignmentSliceState], state => state.level);
const selectContainer = createSelector([selectResourcesAssignmentSliceState], state => state.container);
const selectGrantAccessToAllFutureResources = createSelector(
    [selectResourcesAssignmentSliceState],
    state => state.grantAccessToAllFutureResources
);

export const {
    changeSelectedMenu,
    toggleResourceSelectionScreen,
    changeActiveConnectedAppId,
    changeActiveRoleId,
    changeShowResourceAssignmentSuccessAlert,
    changeLevel,
    changeSelectedRoleInfo,
    prepopulateAssignedResources,
    changeContainer,
    changeGrantAccessToAllFutureResources,
    changeSelectAllResources,
    resetResourceAssignmentStateOnDrawerClose,
    resetStateOnResourceAssignmentScreenToggle,
    fetchCompanies,
    fetchCompaniesSuccess,
    fetchCompaniesFailure,
    fetchInheritedCompanies,
    fetchInheritedCompaniesSuccess,
    fetchInheritedCompaniesFailure,
    fetchLevels,
    fetchLevelsSuccess,
    fetchLevelsFailure,
    fetchAllContainers,
    fetchAllContainersSuccess,
    fetchAllContainersFailure,
    fetchContainersByLevels,
    fetchContainersByLevelsSuccess,
    fetchContainersByLevelsFailure,
    changeInheritedContainers,
    changeSelectedCompaniesIds,
    changeSelectedCompanies,
    fetchSites,
    fetchSitesSuccess,
    fetchSitesFailure,
    fetchInheritedSites,
    fetchInheritedSitesSuccess,
    fetchInheritedSitesFailure,
    changeSelectedSites,
    changeSelectedSitesIds,
    saveOrUpdateAssignedResource,
    changeInheritedSites,
    changeInheritedCompanies,
    changeInheritedSitesCount,
    changeInheritedCompaniesCount,
} = slice.actions;

export { selectContainer, selectGrantAccessToAllFutureResources, selectLevel, selectSelectedMenu };

export default slice.reducer;
